import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useNavigate, useParams } from "react-router-dom";
import { useGuides } from "../hooks/useGuide";
import { useIntegration } from "../hooks/useIntegration";
import { format } from "date-fns";
import { useUserMsp } from "../hooks/useUserMsp";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import WorkflowDiagram from "./WorkflowDiagram";
import { renderFormattedContent } from "../utils/htmlUtils";
import { OVERLAY_SAFE_MENU_PROPS } from "../utils/overlayMenu";

// Type assertion to fix React 18 compatibility issue with react-syntax-highlighter
const TypedSyntaxHighlighter = SyntaxHighlighter as any;

const GuideViewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    guides,
    selectedGuide,
    fetchGuideById,
    isLoading: guidesLoading,
    selectedCustomerId,
    setCustomerSelection,
  } = useGuides();
  const {
    customers,
    getCustomers,
    isLoading: customersLoading,
  } = useIntegration();
  const { userMspInfo } = useUserMsp();

  // State for expandable sections
  const [workflowCodeExpanded, setWorkflowCodeExpanded] = useState(false);
  const [workflowDiagramExpanded, setWorkflowDiagramExpanded] = useState(false);
  const [codeFullscreen, setCodeFullscreen] = useState(false);
  const [testsModalOpen, setTestsModalOpen] = useState(false);
  const [deployMenuAnchor, setDeployMenuAnchor] = useState<null | HTMLElement>(null);

  // Mock deployment status - in real app, this would come from the guide data
  const deploymentStatus = ["Sandbox", "Production"]; // or ["Sandbox"] or ["Production"] or []

  // Mock recommendations
  const recommendations = [
    "Make data transfer instructions more specific",
    "Add error handling for network timeouts",
    "Include rollback procedures for failed deployments",
    "Specify required permissions for each step",
  ];

  // Mock test cases
  const mockTestCases = [
    {
      id: 1,
      name: "Happy Path Test",
      scenario: "Execute workflow with valid inputs and optimal conditions",
      outcome: "Workflow completes successfully with expected results",
    },
    {
      id: 2,
      name: "Invalid Input Handling",
      scenario: "Provide invalid or malformed input data to the workflow",
      outcome: "Workflow validates input and returns appropriate error message",
    },
    {
      id: 3,
      name: "Network Timeout Recovery",
      scenario: "Simulate network timeout during data transfer step",
      outcome: "Workflow retries operation and logs timeout event",
    },
    {
      id: 4,
      name: "Insufficient Permissions",
      scenario: "Execute workflow with user lacking required permissions",
      outcome: "Workflow fails gracefully with permission denied error",
    },
    {
      id: 5,
      name: "Concurrent Execution",
      scenario: "Run multiple instances of workflow simultaneously",
      outcome: "Each instance executes independently without conflicts",
    },
    {
      id: 6,
      name: "Rollback on Failure",
      scenario: "Trigger failure midway through workflow execution",
      outcome: "Workflow performs rollback and restores previous state",
    },
  ];

  // Filter customers by MSP ID and include 'root' customer (displayed as "All")
  const filteredCustomers = React.useMemo(() => {
    let arr = (customers || []).filter((c: any) => {
      return (
        c.name?.toLowerCase() === "root" ||
        String(c.msp_id) === String(userMspInfo?.msp_id)
      );
    });
    // Ensure 'All' (root) is first
    arr = [
      ...arr.filter((c: any) => c.name?.toLowerCase() === "root"),
      ...arr.filter((c: any) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c)),
    ];
    return arr;
  }, [customers, userMspInfo?.msp_id]);

  // Find guide - prefer selectedGuide over guides array
  const guide = selectedGuide || guides.find((g: any) => g.id === Number(id));

  // Fetch customers when component mounts
  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Fetch specific guide when mounting
  useEffect(() => {
    if (id) {
      fetchGuideById(id);
    }
  }, [id, fetchGuideById]);

  // Update Redux state when guide's customer changes
  useEffect(() => {
    if (guide?.customer_id) {
      setCustomerSelection(guide.customer_id.toString());
    }
  }, [guide?.customer_id, setCustomerSelection]);

  // Check if we're still loading data
  const isLoading = guidesLoading || customersLoading;

  // Get customer name
  const getCustomerName = (customerId: any) => {
    const customer = filteredCustomers.find((c: any) => c.id === customerId);
    if (customer?.name?.toLowerCase() === "root") {
      return "All";
    }
    return customer?.name || customerId;
  };

  // Show loading state if we're still fetching data
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1, fontFamily: "var(--theo-font-sans)" }}>
            Guide Details
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)" }}>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  // Show error state if no guide data found
  if (!guide) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1, fontFamily: "var(--theo-font-sans)" }}>
            Guide Details
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error" sx={{ fontFamily: "var(--theo-font-sans)" }}>
            Guide not found
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}
            sx={{ mt: 2, fontFamily: "var(--theo-font-sans)" }}
          >
            Back to Guides
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        mx: 1,
        my: 1,
        p: { xs: 2, sm: 3, md: 4 },
        background: "#FFF",
        borderRadius: 1,
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => navigate("/guides", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1, fontFamily: "var(--theo-font-sans)" }}>
            Guide Details
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/guides/${id}/edit`)}
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          Edit
        </Button>
      </Box>

      {/* Created By */}
      {guide.created_by && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, px: 1 }}>
          <Typography sx={{ fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#838383", fontSize: 14 }}>
            Created by:
          </Typography>
          <Typography sx={{ ml: 1, fontFamily: "var(--theo-font-sans)", fontSize: 14 }}>
            {guide.created_by}
          </Typography>
          {guide.date_added && (
            <>
              <Typography sx={{ mx: 1, fontFamily: "var(--theo-font-sans)", color: "#838383", fontSize: 14 }}>
                •
              </Typography>
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, color: "#838383" }}>
                {format(new Date(guide.date_added), "PPP")}
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Customer */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A" }}>
          Customer
        </Typography>
        <Typography sx={{ fontFamily: "var(--theo-font-sans)" }}>
          {getCustomerName(guide.customer_id)}
        </Typography>
      </Box>

      {/* Type */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A" }}>
          Type
        </Typography>
        <Chip 
          label={guide.type} 
          sx={{ 
            fontFamily: "var(--theo-font-sans)",
            backgroundColor: "#F0F0F0",
            fontWeight: 500
          }} 
        />
      </Box>

      {/* Platform */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A" }}>
          Platform
        </Typography>
        <Chip 
          label={guide.platform} 
          sx={{ 
            fontFamily: "var(--theo-font-sans)",
            backgroundColor: "#F0F0F0",
            fontWeight: 500
          }} 
        />
      </Box>

      {/* Problem Description */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A", pt: 0.5 }}>
          Problem Description
        </Typography>
        <Typography sx={{ flex: 1, fontFamily: "var(--theo-font-sans)", lineHeight: 1.6 }}>
          {guide.description || "N/A"}
        </Typography>
      </Box>

      {/* Steps */}
      {guide.steps && (
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A", pt: 0.5 }}>
            Steps
          </Typography>
          <Box 
            component="div"
            sx={{ 
              flex: 1, 
              fontFamily: "var(--theo-font-sans)",
              lineHeight: 1.6,
              backgroundColor: "#FAFAFA",
              padding: 2,
              borderRadius: 1,
              border: "1px solid #E0E0E0",
              minHeight: "450px", // ~16 lines tall
              maxHeight: "450px",
              overflowY: "auto",
              '& *': {
                fontFamily: "var(--theo-font-sans)",
              },
              '& p': {
                margin: 0,
                marginBottom: '0.5em',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: renderFormattedContent(guide.steps) }}
          />
        </Box>
      )}

      {/* Validation */}
      {guide.validation && (
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A", pt: 0.5 }}>
            Validation
          </Typography>
          <Box 
            sx={{ 
              flex: 1, 
              fontFamily: "var(--theo-font-sans)",
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
              backgroundColor: "#FAFAFA",
              padding: 2,
              borderRadius: 1,
              border: "1px solid #E0E0E0"
            }}
          >
            {guide.validation}
          </Box>
        </Box>
      )}

      {/* Take an Action Section - Only show for workflows */}
      {guide.workflow && guide.workflow.python_code && (
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A", pt: 0.5 }}>
            Take an Action
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: 2,
                padding: 3,
              }}
            >
              {/* Header with Deployment Status */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  Recommendations
                </Typography>
                {deploymentStatus.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 12,
                        color: "#6B7280",
                        mr: 0.5,
                      }}
                    >
                      Deployed to:
                    </Typography>
                    {deploymentStatus.map((status) => (
                      <Chip
                        key={status}
                        label={status}
                        size="small"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: 11,
                          height: 22,
                          backgroundColor: status === "Production" ? "#10B981" : "#3B82F6",
                          color: "#FFFFFF",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Recommendations List */}
              <Box sx={{ mb: 3 }}>
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: 2.5,
                    "& li": {
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: 14,
                      color: "#374151",
                      lineHeight: 1.8,
                      mb: 0.5,
                    },
                  }}
                >
                  {recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={() => setTestsModalOpen(true)}
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    textTransform: "none",
                    borderColor: "#D1D5DB",
                    color: "#374151",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                      backgroundColor: "#F9FAFB",
                    },
                  }}
                >
                  Tests
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    textTransform: "none",
                    borderColor: "#D1D5DB",
                    color: "#374151",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                      backgroundColor: "#F9FAFB",
                    },
                  }}
                >
                  Request Audit
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setDeployMenuAnchor(e.currentTarget)}
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    textTransform: "none",
                  }}
                >
                  Deploy
                </Button>
                <Menu
                  anchorEl={deployMenuAnchor}
                  open={Boolean(deployMenuAnchor)}
                  onClose={() => setDeployMenuAnchor(null)}
                  {...OVERLAY_SAFE_MENU_PROPS}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setDeployMenuAnchor(null);
                      // Handle sandbox deployment
                      console.log("Deploy to Sandbox");
                    }}
                    sx={{ fontFamily: "var(--theo-font-sans)" }}
                  >
                    Sandbox
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setDeployMenuAnchor(null);
                      // Handle production deployment
                      console.log("Deploy to Production");
                    }}
                    sx={{ fontFamily: "var(--theo-font-sans)" }}
                  >
                    Production
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Code */}
      {guide.workflow && guide.workflow.python_code && (
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A", pt: 0.5 }}>
            Code
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
                cursor: "pointer",
                padding: "8px 12px",
                backgroundColor: "#F5F5F5",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#ECECEC",
                },
              }}
              onClick={() => setWorkflowCodeExpanded(!workflowCodeExpanded)}
            >
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#333",
                }}
              >
                {workflowCodeExpanded ? "Hide" : "Show"} Code
              </Typography>
              {workflowCodeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={workflowCodeExpanded}>
              <Box 
                sx={{ 
                  flex: 1,
                  borderRadius: 1,
                  border: "1px solid #E0E0E0",
                  overflow: "hidden",
                  backgroundColor: "#1E1E1E",
                  height: "600px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#252526",
                    padding: "8px 16px",
                    borderBottom: "1px solid #3E3E42",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 12,
                        color: "#CCCCCC",
                        fontWeight: 500,
                      }}
                    >
                      {guide.workflow.workflow_name || "Workflow"}
                    </Typography>
                    <Chip
                      label="Python"
                      size="small"
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 10,
                        height: 20,
                        backgroundColor: "#0E639C",
                        color: "#FFFFFF",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCodeFullscreen(true);
                    }}
                    sx={{ color: "#CCCCCC" }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    "& pre": {
                      margin: 0,
                      height: "100%",
                    },
                  }}
                >
                  <TypedSyntaxHighlighter
                    language="python"
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      padding: "16px",
                      fontSize: "13px",
                      fontFamily: "Consolas, 'Courier New', monospace",
                      lineHeight: "1.5",
                      backgroundColor: "#1E1E1E",
                      height: "100%",
                    }}
                    showLineNumbers
                    lineNumberStyle={{
                      color: "#858585",
                      marginRight: "16px",
                      minWidth: "3em",
                    }}
                  >
                    {guide.workflow.python_code}
                  </TypedSyntaxHighlighter>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      )}

      {/* Diagram */}
      {guide.workflow && guide.workflow.python_code && (
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#705D4A", pt: 0.5 }}>
            Diagram
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
                cursor: "pointer",
                padding: "8px 12px",
                backgroundColor: "#F5F5F5",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#ECECEC",
                },
              }}
              onClick={() => setWorkflowDiagramExpanded(!workflowDiagramExpanded)}
            >
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#333",
                }}
              >
                {workflowDiagramExpanded ? "Hide" : "Show"} Diagram
              </Typography>
              {workflowDiagramExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={workflowDiagramExpanded}>
              <WorkflowDiagram pythonCode={guide.workflow.python_code} />
            </Collapse>
          </Box>
        </Box>
      )}

      {/* Last Updated */}
      {guide.last_updated && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 4, pt: 3, borderTop: "1px solid #E0E0E0" }}>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#838383" }}>
            Last updated: {format(new Date(guide.last_updated), "PPP")}
          </Typography>
        </Box>
      )}

      {/* Fullscreen Code Dialog */}
      <Dialog
        open={codeFullscreen}
        onClose={() => setCodeFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "95vh",
            maxWidth: "none",
            maxHeight: "none",
            m: 2,
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#1E1E1E",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#252526",
              padding: "12px 20px",
              borderBottom: "1px solid #3E3E42",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 14,
                  color: "#CCCCCC",
                  fontWeight: 500,
                }}
              >
                {guide.workflow?.workflow_name || "Workflow"}
              </Typography>
              <Chip
                label="Python"
                size="small"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 10,
                  height: 20,
                  backgroundColor: "#0E639C",
                  color: "#FFFFFF",
                  fontWeight: 500,
                }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setCodeFullscreen(false)}
              sx={{ color: "#CCCCCC" }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              "& pre": {
                margin: 0,
                height: "100%",
              },
            }}
          >
            <TypedSyntaxHighlighter
              language="python"
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                padding: "20px",
                fontSize: "14px",
                fontFamily: "Consolas, 'Courier New', monospace",
                lineHeight: "1.6",
                backgroundColor: "#1E1E1E",
                height: "100%",
              }}
              showLineNumbers
              lineNumberStyle={{
                color: "#858585",
                marginRight: "20px",
                minWidth: "3em",
              }}
            >
              {guide.workflow?.python_code || ""}
            </TypedSyntaxHighlighter>
          </Box>
        </Box>
      </Dialog>

      {/* Tests Modal */}
      <Dialog
        open={testsModalOpen}
        onClose={() => setTestsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 20,
            fontWeight: 600,
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
          }}
        >
          Test Cases
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 600,
                      color: "#374151",
                      backgroundColor: "#F9FAFB",
                      width: "60px",
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 600,
                      color: "#374151",
                      backgroundColor: "#F9FAFB",
                      width: "200px",
                    }}
                  >
                    Test Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 600,
                      color: "#374151",
                      backgroundColor: "#F9FAFB",
                    }}
                  >
                    Scenario
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 600,
                      color: "#374151",
                      backgroundColor: "#F9FAFB",
                    }}
                  >
                    Expected Outcome
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockTestCases.map((testCase) => (
                  <TableRow
                    key={testCase.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 14,
                        color: "#6B7280",
                      }}
                    >
                      {testCase.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#111827",
                      }}
                    >
                      {testCase.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 14,
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      {testCase.scenario}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 14,
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      {testCase.outcome}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => setTestsModalOpen(false)}
              sx={{
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default GuideViewPage;
